import { useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { ArrowRight, Check, CheckCheck, MailOpen, Settings, X } from 'lucide-react'
import { Button } from '../button/Button.tsx'
import LogoMarkWhite from '../brand/assets/logo-mark-white.svg'
import { SwipeableRow } from '../swipeable-row/SwipeableRow.tsx'
import { useInfiniteScroll } from '../infinite-scroll/useInfiniteScroll.ts'
import { InfiniteScrollFooter } from '../infinite-scroll/InfiniteScrollFooter.tsx'
import type { Notification } from './types.ts'
import { buildSections } from './rows.ts'
import type { NotificationRow } from './rows.ts'
import { notificationAction } from './actions.ts'
import { notificationTypeIcon } from './typeIcons.ts'
import { relativeTime } from './time.ts'
import styles from './NotificationCenter.module.css'

export interface NotificationCenterProps {
  notifications: Notification[]
  unreadCount: number
  onNotificationClick?: (notification: Notification) => void
  onMarkRead?: (notificationId: string) => void
  onMarkAllRead?: () => void
  /** Called when X button is clicked (should show confirm dialog) */
  onDismiss?: (notificationId: string) => void
  /** Called when swipe-to-delete completes (should show undo toast) */
  onSwipeDismiss?: (notificationId: string) => void
  /**
   * Called when a row's action button (Review, Respond, Rule on it…) is used,
   * with where that action lives. Without it, rows show no action button.
   */
  onAction?: (notification: Notification, to: string) => void
  onViewAll?: () => void
  onSettings?: () => void
  /** Pagination */
  hasMore?: boolean
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

/** Who sent it: the org's logo, its initial, or the Carousel mark for platform notices (no org). */
function SourceTile({ n, children }: { n: Notification; children: ReactNode }) {
  let tile: ReactNode
  if (!n.org.name) {
    tile = <span className={`${styles.tile} ${styles.tileCarousel}`}><img src={LogoMarkWhite} alt="" className={styles.tileMark} /></span>
  } else if (n.org.logoUrl) {
    tile = <img src={n.org.logoUrl} alt="" className={styles.tile} />
  } else {
    tile = <span className={`${styles.tile} ${styles.tileInitial}`}>{n.org.name.charAt(0)}</span>
  }
  return <span className={styles.source}>{tile}<span className={styles.kind}>{children}</span></span>
}

/**
 * The bell's drawer. It holds unread notifications only: once one is read it
 * leaves the drawer and lives on the full notifications page (View all).
 */
export function NotificationCenter({
  notifications,
  unreadCount,
  onNotificationClick,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onSwipeDismiss,
  onAction,
  onViewAll,
  onSettings,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: NotificationCenterProps) {
  const listRef = useRef<HTMLDivElement>(null)
  const unread = useMemo(() => notifications.filter((n) => !n.read), [notifications])
  const sections = useMemo(() => buildSections(unread), [unread])
  const isEmpty = sections.length === 0

  const { pullDistance } = useInfiniteScroll({
    scrollRef: listRef,
    onLoadMore: onLoadMore ?? (() => undefined),
    hasMore,
    isLoading: isLoadingMore,
    threshold: 0.3,
    rubberBand: true,
  })

  // Pages arrive newest first whether read or not, so unread notices can sit
  // behind a page of read ones. Keep loading until the drawer has them all.
  useEffect(() => {
    if (hasMore && !isLoadingMore && unread.length < unreadCount) onLoadMore?.()
  }, [hasMore, isLoadingMore, unread.length, unreadCount, onLoadMore])

  const markRowRead = (row: NotificationRow) => row.ids.forEach((id) => onMarkRead?.(id))

  return (
    <div className={styles.panel} role="dialog" aria-label="Notifications">
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerTitle}>Notifications</span>
          {unreadCount > 0 && <span className={styles.headerCount}>{unreadCount} unread</span>}
        </div>
        <div className={styles.headerActions}>
          {unreadCount > 0 && (
            <button
              type="button"
              className={styles.headerBtn}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onMarkAllRead?.() }}
              aria-label="Mark all as read"
              title="Mark all as read"
            >
              <CheckCheck size={16} />
            </button>
          )}
          <button
            type="button"
            className={styles.headerBtn}
            onClick={onSettings}
            aria-label="Notification settings"
            title="Notification settings"
          >
            <Settings size={16} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className={styles.list} ref={listRef}>
        {isEmpty ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}><Check size={20} /></span>
            <span className={styles.emptyTitle}>You&apos;re all caught up</span>
            <span className={styles.emptyBody}>Notifications you&apos;ve read are on the notifications page.</span>
          </div>
        ) : (
          sections.map((section) => (
            <section key={section.label} aria-label={section.label}>
              <h3 className={styles.day}>{section.label}</h3>
              {section.rows.map((row) => {
                const n = row.lead
                const Icon = notificationTypeIcon(n.type)
                const action = onAction ? notificationAction(n) : null
                return (
                  <SwipeableRow
                    key={n.id}
                    onDelete={() => row.ids.forEach((id) => (onSwipeDismiss ?? onDismiss)?.(id))}
                    onAction={() => markRowRead(row)}
                    showAction
                    actionLabel="Read"
                    deleteLabel="Delete"
                    className={styles.row}
                  >
                    <div className={styles.item} role="group" aria-label={n.title}>
                      <span className={styles.unreadDot} aria-hidden="true" />
                      <button
                        type="button"
                        className={styles.itemMain}
                        onClick={() => {
                          markRowRead(row)
                          onNotificationClick?.(n)
                        }}
                      >
                        <SourceTile n={n}><Icon size={10} strokeWidth={2.4} /></SourceTile>
                        <span className={styles.itemContent}>
                          <span className={styles.itemTop}>
                            <span className={styles.itemTitle}>
                              {n.title}
                              {row.count > 1 && <span className={styles.repeat}>×{row.count}</span>}
                            </span>
                            <span className={styles.time}>{relativeTime(n.createdAt)}</span>
                          </span>
                          <span className={styles.itemMeta}>
                            <span className={styles.orgName}>{n.org.name || 'Carousel'}</span>
                            {n.body && <> · {n.body}</>}
                          </span>
                        </span>
                      </button>
                      {action && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className={styles.rowAction}
                          onClick={() => {
                            markRowRead(row)
                            onAction?.(n, action.to)
                          }}
                        >
                          <Icon size={14} aria-hidden="true" />
                          {action.label}
                        </Button>
                      )}
                      <div className={styles.itemActions}>
                        <button
                          type="button"
                          className={styles.itemActionBtn}
                          onClick={(e) => { e.stopPropagation(); markRowRead(row) }}
                          aria-label="Mark as read"
                          title="Mark as read"
                        >
                          <MailOpen size={13} />
                        </button>
                        <button
                          type="button"
                          className={styles.itemActionBtn}
                          onClick={(e) => { e.stopPropagation(); row.ids.forEach((id) => onDismiss?.(id)) }}
                          aria-label="Dismiss notification"
                          title="Dismiss"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    </div>
                  </SwipeableRow>
                )
              })}
            </section>
          ))
        )}
        {!isEmpty && (hasMore || isLoadingMore) && (
          <InfiniteScrollFooter
            isLoading={isLoadingMore}
            hasMore={hasMore}
            pullDistance={pullDistance}
          />
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button type="button" className={styles.footerLink} onClick={onViewAll}>
          See all notifications
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  )
}
